// Team Shared Datadrive Page
// Accessible by all team members with role-based permissions
// Admin/Owner: Read-Write (TeamBoxReadWrite)
// Normal Member: Read-Only (TeamBoxReadOnly)

import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Button, Divider, Loading, message, Trans } from '@/components'
import { Dropdown } from '@/components/base/Dropdown'
import { InsufficientQuotaView } from '@/components/InsufficientQuotaView'
import { TeamRole } from '@/consts/team'
import { BulkActionBar } from '@/pages/data-mgr/components/BulkActionBar'
import { DeleteConfirmModal } from '@/pages/data-mgr/components/DeleteConfirmModal'
import { FileGallery } from '@/pages/data-mgr/components/FileGallery'
import { FileTable } from '@/pages/data-mgr/components/FileTable'
import { PathBreadcrumb } from '@/pages/data-mgr/components/PathBreadcrumb'
import { TransferStatusIndicator } from '@/pages/data-mgr/components/TransferStatusIndicator'
import { ViewMode, ViewToggle } from '@/pages/data-mgr/components/ViewToggle'
import { IFile } from '@/pages/data-mgr/core/types'
import { useTeamStore, useTeamUserRole } from '@/stores/team'
import { formatDomain } from '@/utils/formatter'
import { cn } from '@/utils/style'

import { DataViewSwitcher } from '../components/DataViewSwitcher'
import { MemberViewSwitcher } from '../components/MemberViewSwitcher'
import TeamCreateFolder from './TeamCreateFolder'
import { useTeamDownload } from './useTeamDownload'
import { useTeamFileManager } from './useTeamFileManager'

export function SharedDataView() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const userRole = useTeamUserRole()
  const teamTokenData = useTeamStore((state) => state.teamTokenData)
  const [viewMode, setViewMode] = useState<ViewMode>('list')

  // Initialize file manager with team-specific params
  // TeamLayout already validates team context, so these params are guaranteed to be valid
  const manager = useTeamFileManager({
    basePath: '/team/data/shared',
    userRole: userRole || TeamRole.Normal,
    teamId: teamTokenData?.teamId || 0,
    teamResID: teamTokenData?.teamResID || '',
  })

  // Download hook with progress tracking
  const { downloadList, downloadFile, downloadAsZip, cancelDownload } = useTeamDownload(
    userRole || TeamRole.Normal,
    teamTokenData?.teamId || 0,
  )

  // Single file download handler
  const handleSingleDownload = (file: IFile) => {
    downloadFile(file, manager.currentDomain)
  }

  // Bulk action handlers
  const handleBulkDownload = async () => {
    const filesToDownload = manager.selectedRows.filter((f) => !f.isDir)
    if (filesToDownload.length === 0) {
      message.show('No files selected for download')
      return
    }
    downloadAsZip(filesToDownload, manager.currentDomain)
  }

  const handleBulkMove = () => {
    manager.setMoveInEdit(manager.selectedRows)
  }

  const handleBulkRename = () => {
    if (manager.selectedRows.length === 1) {
      manager.setRenameInEdit(manager.selectedRows[0])
    }
  }

  const handleBulkDelete = () => {
    manager.setDeleteModalOpen(true)
  }

  const handleRefresh = async () => {
    const success = await manager.fetchFiles(manager.dirPath, manager.currentDomain)
    if (success) message.success(t('data.updateCompleted'))
  }

  return (
    <div className="flex flex-col h-full gap-4">
      {/* Datadrive File Manager */}
      <div className="card bg-panel-white h-full overflow-y-auto relative">
        {/* Header */}
        <div className="flex h-[60px] items-start justify-start gap-4">
          <div className="flex items-center gap-2">
            <div className="h6">
              <Trans i18nKey="data.title">Datadrive</Trans>
            </div>
            {/* Admin/Owner shows DataViewSwitcher, Normal Member shows MemberViewSwitcher */}
            {manager.canEdit ? <DataViewSwitcher /> : <MemberViewSwitcher />}
          </div>

          {/* Transfer Status Indicator */}
          <TransferStatusIndicator
            uploadTasks={manager.uploadList}
            downloadTasks={downloadList}
            onPauseUpload={manager.pauseTask}
            onResumeUpload={manager.resumeTask}
            onCancelDownload={cancelDownload}
          />

          {/* Region Dropdown - right side */}
          {manager.domainInfos.length > 0 && (
            <div className="ml-auto flex items-center gap-4">
              <Dropdown
                menuClassName="w-40"
                rootClassName="t-sb w-40"
                className="h-11"
                options={manager.domainInfos.map((info) => ({
                  label: info.displayName || formatDomain(info.domain),
                  value: info.domain,
                  className: 't-sm',
                }))}
                value={manager.currentDomain}
                onChange={(value) => manager.onDomainChange(Number(value))}
                placement="left"
              />
            </div>
          )}
        </div>

        <Divider />

        {/* File manager content */}
        {manager.loading ? (
          <div className="w-full flex items-center justify-center p-8">
            <Loading />
          </div>
        ) : manager.domainInfos.length === 0 || manager.notAvailable ? (
          <div className="mt-4 mb-2">
            <InsufficientQuotaView link="/team/space/shared" />
          </div>
        ) : (
          <div className="pb-20">
            {/* Action bar */}
            <div className="flex items-end justify-between">
              {/* Action buttons - visible for all roles */}
              <div className="flex gap-2">
                <Button onClick={handleRefresh}>
                  <Trans i18nKey="data.refreshBtn">Refresh</Trans>
                </Button>
                <TeamCreateFolder
                  dir={manager.dirPath}
                  files={manager.files}
                  onOk={() => manager.fetchFiles(manager.dirPath, manager.currentDomain)}
                  domain={manager.currentDomain}
                  userRole={manager.userRole}
                  teamId={manager.teamId}
                  disabled={!manager.canEdit}
                />
                <label
                  className={cn(
                    'rounded-[8px] t-sb px-4 py-3',
                    manager.canEdit
                      ? 'bg-btn-secondary text-btn-foreground hover:bg-btn-secondary-hover cursor-pointer'
                      : 'bg-btn-secondary/50 text-btn-foreground/50 cursor-not-allowed',
                  )}
                >
                  <Trans i18nKey="data.uploadBtn">Upload</Trans>
                  <input
                    type="file"
                    className="hidden"
                    onChange={manager.canEdit ? manager.onFileChange : undefined}
                    disabled={!manager.canEdit}
                  />
                </label>
              </div>

              {/* View Toggle */}
              <ViewToggle value={viewMode} onChange={setViewMode} />
            </div>

            <PathBreadcrumb
              pathList={manager.pathList}
              onNavigate={(path) => navigate(manager.getPathUrl(path))}
              files={manager.files}
              uploadingTasks={manager.uploadingTasks}
              selectedRows={manager.selectedRows}
            />

            {viewMode === 'list' ? (
              <FileTable
                files={manager.files}
                uploadingTasks={manager.uploadingTasks}
                selectedRows={manager.selectedRows}
                currentDomain={manager.currentDomain}
                disableEdit={manager.isReadOnly}
                onSelectionChange={manager.handleRowSelection}
                onSelectAll={manager.handleSelectAll}
                onFileClick={manager.handleFileClick}
                onDownload={handleSingleDownload}
                onMove={manager.onRowMoveClick}
                onRename={manager.onRowRenameClick}
                onDelete={manager.onRowDeleteClick}
              />
            ) : (
              <FileGallery
                files={manager.files}
                selectedRows={manager.selectedRows}
                onSelectionChange={manager.handleRowSelection}
                onSelectAll={manager.handleSelectAll}
                onFileClick={manager.handleFileClick}
              />
            )}
          </div>
        )}

        {/* Dialogs */}
        <DeleteConfirmModal
          open={manager.deleteModalOpen}
          onClose={() => manager.setDeleteModalOpen(false)}
          onConfirm={manager.onFileDeleteClick}
          loading={manager.loading}
        />
        <manager.RenameDialog
          file={manager.renameInEdit}
          files={
            manager.renameInEdit && Array.isArray(manager.files)
              ? manager.files.filter((f) => f.id != manager.renameInEdit!.id)
              : []
          }
          onCancel={() => manager.setRenameInEdit(undefined)}
          onOk={() => {
            manager.setRenameInEdit(undefined)
            manager.fetchFiles(manager.dirPath, manager.currentDomain)
          }}
        />
        {Array.isArray(manager.moveInEdit) && manager.moveInEdit.length > 0 ? (
          <manager.MoveDialog
            onCancel={() => manager.setMoveInEdit(undefined)}
            onOk={() => {
              manager.setMoveInEdit(undefined)
              manager.fetchFiles(manager.dirPath, manager.currentDomain)
            }}
            files={manager.moveInEdit}
            currentPath={manager.dirPath}
          />
        ) : null}

        {/* Floating Bulk Action Bar */}
        <BulkActionBar
          selectedFiles={manager.selectedRows}
          onDownload={handleBulkDownload}
          onMove={handleBulkMove}
          onRename={handleBulkRename}
          onDelete={handleBulkDelete}
          disableEdit={manager.isReadOnly}
        />
      </div>
    </div>
  )
}
